"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import SearchableSelect from "@/components/ui/SearchableSelect";
import Input from "@/components/form/input/InputField";
import { Modal } from "@/components/ui/modal/index";
import Label from "@/components/form/Label";
import { handleApiError } from "@/lib/api";
import { PencilIcon } from "@/icons";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";

const DEFAULT_UOM = "CUM";
const UOM_OPTIONS = ["CUM", "TON", "KG", "MT", "NOS"];

export default function RmcGrades() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [partyId, setPartyId] = React.useState(null);
  const [name, setName] = React.useState("");
  const [uom, setUom] = React.useState(DEFAULT_UOM);
  const selectedPartyId = partyId != null ? Number(partyId) : null;

  const { data: parties = [] } = useQuery({
    queryKey: ["salesParties"],
    queryFn: async () => (await rmcClient.listSalesParties()).data,
  });

  const { data: rows = [] } = useQuery({
    queryKey: ["grades", selectedPartyId],
    queryFn: async () => (await rmcClient.listGrades({ party_id: selectedPartyId })).data,
    enabled: selectedPartyId != null,
  });

  const uomOptionRows = React.useMemo(() => {
    const v = (uom || DEFAULT_UOM).trim();
    const codes = UOM_OPTIONS.includes(v) ? UOM_OPTIONS : [v, ...UOM_OPTIONS];
    return codes.map((code) => ({ id: code, name: code }));
  }, [uom]);

  const openAdd = () => {
    setEditingId(null);
    setName("");
    setUom(DEFAULT_UOM);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setName(row.name ?? "");
    setUom(row.uom ?? DEFAULT_UOM);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setName("");
    setUom(DEFAULT_UOM);
  };

  const save = useMutation({
    mutationFn: () => {
      if (!name.trim()) throw new Error("empty");
      if (selectedPartyId == null) throw new Error("party");
      if (editingId != null) {
        return rmcClient.updateGrade(editingId, {
          party_id: selectedPartyId,
          name: name.trim(),
          uom: uom || DEFAULT_UOM,
        });
      }
      return rmcClient.createGrade({
        party_id: selectedPartyId,
        name: name.trim(),
        uom: uom || DEFAULT_UOM,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grades", selectedPartyId] });
      toast.success(editingId != null ? "Grade updated" : "Grade added");
      closeModal();
    },
    onError: (e) => {
      if (e.message === "empty") toast.error("Name is required");
      else if (e.message === "party") toast.error("Select a sales party first");
      else toast.error(handleApiError(e));
    },
  });

  return (
    <div className="space-y-6">
      <PageBreadCrumb pageTitle="Grade Master" />
      <div className="rounded-lg border border-gray-800 bg-gray-900/40 p-4">
        <SearchableSelect
          label="Sales Party"
          value={partyId}
          onChange={(id) => {
            setPartyId(id ?? null);
            setModalOpen(false);
            setEditingId(null);
          }}
          options={parties}
          getLabel={(p) => p.name}
          getValue={(p) => p.id}
          getSubLabel={(p) => p.gst_number}
          placeholder="Select sales party…"
        />
      </div>
      <div className="flex justify-end">
        <Button size="sm" onClick={openAdd} disabled={selectedPartyId == null}>
          Add Grade
        </Button>
      </div>
      {selectedPartyId == null && (
        <p className="text-sm text-gray-500">Select a party to manage grades.</p>
      )}
      <DataTable
        columns={[
          { key: "name", header: "Name" },
          {
            key: "uom",
            header: "UOM",
            render: (row) => row.uom ?? DEFAULT_UOM,
          },
          {
            key: "actions",
            header: "",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: (row) => (
              <button
                type="button"
                aria-label={`Edit grade ${row.name ?? row.id}`}
                title="Edit"
                disabled={save.isPending}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white disabled:opacity-50"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(row);
                }}
              >
                <PencilIcon className="size-4" />
              </button>
            ),
          },
        ]}
        data={selectedPartyId == null ? [] : rows}
        isLoading={false}
        emptyMessage={
          selectedPartyId == null
            ? "Select a sales party to view grades."
            : "No grades found for this party."
        }
      />
      <Modal isOpen={modalOpen} onClose={closeModal} className="max-w-md overflow-visible!">
        <h3 className="pr-10 text-base font-semibold text-white">
          {editingId != null ? "Edit grade" : "Add grade"}
        </h3>
        <div className="mt-4 space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>UOM</Label>
            <div className="mt-1">
              <SearchableSelect
                value={uom}
                onChange={(v) => {
                  if (v != null) setUom(String(v));
                }}
                options={uomOptionRows}
                getLabel={(o) => o.name}
                getValue={(o) => o.id}
                placeholder="Search or select UOM…"
              />
            </div>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button size="sm" variant="outline" onClick={closeModal}>
            Cancel
          </Button>
          <Button size="sm" disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>
            {editingId != null ? "Update" : "Save"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
